import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import WeekView, { DAYS, MEAL_TYPES } from './components/WeekView';
import MonthView from './components/MonthView';
import Pantry from './components/Pantry';
import ShoppingList from './components/ShoppingList';
import SharedShoppingList from './components/SharedShoppingList';
import ReceiptImport from './components/ReceiptImport';
import Recipes from './components/Recipes';
import KitchenBar from './components/KitchenBar';
import RankDetail from './components/RankDetail';
import InstallPrompt from './components/InstallPrompt';
import SchemeToggle from './components/SchemeToggle';
import WelcomeGuide from './components/WelcomeGuide';
import { useAuth } from './components/AuthProvider';
import { isPushSupported, isPushSubscribed, getVapidPublicKey, subscribeToPush } from './utils/notifications';
import './App.css';

// ========== Random tagline ==========

function useRandomTagline(t) {
  const taglines = t('app.taglines', { returnObjects: true });
  const list = Array.isArray(taglines) ? taglines : [taglines];
  const [text] = useState(() => list[Math.floor(Math.random() * list.length)]);
  return text;
}

// ========== Week helpers ==========

function getISOWeekString(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
}

function getMondayOfWeek(weekStr) {
  const match = weekStr.match(/^(\d{4})-W(\d{2})$/);
  if (!match) return new Date();
  const year = parseInt(match[1]);
  const week = parseInt(match[2]);
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const dayOfWeek = jan4.getUTCDay() || 7;
  const mondayOfWeek1 = new Date(jan4);
  mondayOfWeek1.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
  const monday = new Date(mondayOfWeek1);
  monday.setUTCDate(mondayOfWeek1.getUTCDate() + (week - 1) * 7);
  return monday;
}

function navigateWeek(weekStr, offset) {
  const monday = getMondayOfWeek(weekStr);
  monday.setUTCDate(monday.getUTCDate() + offset * 7);
  return getISOWeekString(monday);
}

function getDatesForWeek(weekStr) {
  const monday = getMondayOfWeek(weekStr);
  const dates = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date(monday);
    d.setUTCDate(monday.getUTCDate() + i);
    dates.push(d);
  }
  return dates;
}

function mergeStockAndNeeds(stock, needs) {
  const needsByName = {};
  for (const n of needs) needsByName[n.name] = n;
  const items = [];
  const seen = new Set();
  for (const s of stock) {
    seen.add(s.name);
    const n = needsByName[s.name];
    items.push({
      name: s.name,
      qty: s.qty || 0,
      need: n ? (n.need || 0) : 0,
      section: s.section || 'cupboard',
      is_staple: s.is_staple || false,
      is_from_stock: true,
    });
  }
  for (const n of needs) {
    if (!seen.has(n.name)) {
      items.push({
        name: n.name,
        qty: 0,
        need: n.need || 0,
        section: n.section || 'cupboard',
        is_staple: false,
        is_from_stock: false,
      });
    }
  }
  return items;
}

function App() {
  // Detect shared shopping list route
  const isSharedList = window.location.pathname === '/shared-list' && new URLSearchParams(window.location.search).has('token');
  if (isSharedList) {
    return <SharedShoppingList />;
  }

  return <AppMain />;
}

function AppMain() {
  const { session, loading, activeKitchen, notAllowed, signInWithGoogle, signInWithEmail, signUp, signOut, apiFetch } = useAuth();
  const { t, i18n } = useTranslation();
  const tagline = useRandomTagline(t);
  const [week, setWeek] = useState(() => getISOWeekString(new Date()));
  const [meals, setMeals] = useState(null);
  const [pantry, setPantry] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [activeTab, setActiveTab] = useState('planner');
  const [plannerView, setPlannerView] = useState('week');
  const [monthDate, setMonthDate] = useState(() => {
    const now = new Date();
    return { year: now.getFullYear(), month: now.getMonth() };
  });
  const [pantryView, setPantryView] = useState('pantry');

  const [shareLoading, setShareLoading] = useState(false);
  const [shareToast, setShareToast] = useState(null);
  const [pantrySaveError, setPantrySaveError] = useState('');
  const [showReceiptImport, setShowReceiptImport] = useState(false);
  const [pushPromptVisible, setPushPromptVisible] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [recentWeeks, setRecentWeeks] = useState([]);
  const [requestStatus, setRequestStatus] = useState(null); // null | 'sending' | 'sent' | 'error'
  const [requestError, setRequestError] = useState('');
  const [authMode, setAuthMode] = useState('login'); // 'login' | 'register'
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [authEmail, setAuthEmail] = useState('');
  const [authPassword, setAuthPassword] = useState('');
  const [authConfirm, setAuthConfirm] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authError, setAuthError] = useState('');
  const [authSuccess, setAuthSuccess] = useState('');
  const isLocalhost = window.location.hostname === 'localhost';
  const [captchaToken, setCaptchaToken] = useState(null);
  const turnstileRef = useRef(null);
  const turnstileWidgetId = useRef(null);
  const [rankData, setRankData] = useState(null);
  const [showRankDetail, setShowRankDetail] = useState(false);
  const [xpToast, setXpToast] = useState(null);
  const [welcomeDismissed, setWelcomeDismissed] = useState(() => localStorage.getItem('ratatuil_onboarding_done') === 'true');

  // Hide the static PWA splash screen once React has mounted
  useEffect(() => {
    const splash = document.getElementById('splash-screen');
    if (!splash) return;
    splash.style.opacity = '0';
    const timer = setTimeout(() => splash.remove(), 400);
    return () => clearTimeout(timer);
  }, []);

  // Check push notification state after auth
  useEffect(() => {
    if (!session || !activeKitchen) return;
    if (!isPushSupported()) return;
    const dismissed = localStorage.getItem('push-prompt-dismissed');
    if (dismissed) return;
    isPushSubscribed().then((subscribed) => {
      setPushSubscribed(subscribed);
      if (!subscribed && Notification.permission === 'default') {
        setPushPromptVisible(true);
      }
    });
  }, [session, activeKitchen]);

  const handleEnablePush = async () => {
    const vapidKey = await getVapidPublicKey();
    if (!vapidKey) {
      setPushPromptVisible(false);
      return;
    }
    const result = await subscribeToPush(apiFetch, vapidKey);
    if (result.ok) {
      setPushSubscribed(true);
    }
    setPushPromptVisible(false);
  };

  const handleDismissPush = () => {
    setPushPromptVisible(false);
    localStorage.setItem('push-prompt-dismissed', 'true');
  };

  const handleRequestAccess = async (email) => {
    setRequestStatus('sending');
    setRequestError('');
    try {
      const body = { email, name: '', message: '' };
      const res = await fetch('/api/access-requests', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || t('auth.somethingWrong'));
      }
      setRequestStatus('sent');
    } catch (err) {
      setRequestStatus('error');
      setRequestError(err.message || t('auth.somethingWrong'));
    }
  };

  const handleEmailSignIn = async (e) => {
    e.preventDefault();
    setAuthError('');
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (siteKey && !isLocalhost && !captchaToken) {
      setAuthError(t('auth.captchaRequired'));
      return;
    }
    setAuthLoading(true);
    const { error } = await signInWithEmail(authEmail, authPassword, captchaToken);
    setAuthLoading(false);
    if (error) {
      setAuthError(error.message);
      if (window.turnstile && turnstileWidgetId.current != null) {
        window.turnstile.reset(turnstileWidgetId.current);
        setCaptchaToken(null);
      }
    } else {
      setAuthPassword('');
    }
  };

  const handleEmailSignUp = async (e) => {
    e.preventDefault();
    setAuthError('');
    if (authPassword !== authConfirm) {
      setAuthError(t('auth.passwordMismatch'));
      return;
    }
    if (authPassword.length < 6) {
      setAuthError(t('auth.passwordTooShort'));
      return;
    }
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (siteKey && !isLocalhost && !captchaToken) {
      setAuthError(t('auth.captchaRequired'));
      return;
    }
    setAuthLoading(true);
    const { data, error } = await signUp(authEmail, authPassword, captchaToken);
    setAuthLoading(false);
    if (error) {
      setAuthError(error.message);
      // Reset Turnstile widget so user can retry
      if (window.turnstile && turnstileWidgetId.current != null) {
        window.turnstile.reset(turnstileWidgetId.current);
        setCaptchaToken(null);
      }
    } else if (data?.user?.identities?.length === 0) {
      setAuthError(t('auth.emailAlreadyRegistered'));
    } else {
      setAuthPassword('');
      setAuthConfirm('');
      setCaptchaToken(null);
      setAuthSuccess(t('auth.confirmationSent'));
    }
  };

  const switchAuthMode = (mode) => {
    setAuthMode(mode);
    setAuthError('');
    setAuthSuccess('');
    setCaptchaToken(null);
  };

  // Load Turnstile widget when sign-up form is visible
  useEffect(() => {
    const siteKey = process.env.REACT_APP_TURNSTILE_SITE_KEY;
    if (!siteKey || isLocalhost || !showEmailForm) return;
    let cancelled = false;
    // Load Turnstile script if not present
    if (!document.getElementById('cf-turnstile-script')) {
      const script = document.createElement('script');
      script.id = 'cf-turnstile-script';
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      document.head.appendChild(script);
    }
    // Poll for window.turnstile to be ready, then render
    const interval = setInterval(() => {
      if (cancelled) return;
      if (!window.turnstile || !turnstileRef.current) return;
      if (turnstileWidgetId.current != null) { clearInterval(interval); return; }
      clearInterval(interval);
      turnstileWidgetId.current = window.turnstile.render(turnstileRef.current, {
        sitekey: siteKey,
        callback: (token) => setCaptchaToken(token),
        'expired-callback': () => setCaptchaToken(null),
        theme: 'auto',
      });
    }, 100);
    return () => {
      cancelled = true;
      clearInterval(interval);
      if (window.turnstile && turnstileWidgetId.current != null) {
        window.turnstile.remove(turnstileWidgetId.current);
        turnstileWidgetId.current = null;
      }
    };
  }, [authMode, showEmailForm]);

  const fetchMeals = useCallback(async (w) => {
    if (!activeKitchen) return;
    try {
      const res = await apiFetch(`/api/meals?week=${w}`);
      const data = await res.json();
      setMeals(data);
    } catch (err) {
      console.error('Failed to load meals:', err);
    }
  }, [apiFetch, activeKitchen]);

  const fetchPantry = useCallback(async (w) => {
    if (!activeKitchen) return;
    try {
      const res = await apiFetch(`/api/pantry?week=${w}`);
      const data = await res.json();
      setPantry(data);
    } catch (err) {
      console.error('Failed to load pantry:', err);
    }
  }, [apiFetch, activeKitchen]);

  const fetchRecipes = useCallback(async () => {
    if (!activeKitchen) return;
    try {
      const res = await apiFetch(`/api/recipes`);
      const data = await res.json();
      setRecipes(data);
    } catch (err) {
      console.error('Failed to load recipes:', err);
    }
  }, [apiFetch, activeKitchen]);

  const fetchRecentWeeks = useCallback(async () => {
    if (!activeKitchen) return;
    try {
      const res = await apiFetch('/api/meals/recent?weeks=4');
      const data = await res.json();
      setRecentWeeks(data);
    } catch (err) {
      console.error('Failed to load recent weeks:', err);
    }
  }, [apiFetch, activeKitchen]);

  const fetchRankData = useCallback(async () => {
    if (!activeKitchen) return;
    try {
      const res = await apiFetch(`/api/kitchens/${activeKitchen.id}/ranks`);
      const data = await res.json();
      setRankData(data);
    } catch (err) {
      console.error('Failed to load rank data:', err);
    }
  }, [apiFetch, activeKitchen]);

  useEffect(() => {
    fetchRecipes();
    fetchRecentWeeks();
    fetchRankData();
  }, [fetchRecipes, fetchRecentWeeks, fetchRankData]);

  useEffect(() => {
    setMeals(null);
    setPantry(null);
    fetchMeals(week);
    fetchPantry(week);
  }, [week, fetchMeals, fetchPantry]);

  const computeNeeds = useCallback((mealsData) => {
    const needs = {};
    for (const day of Object.values(mealsData)) {
      for (const slots of Object.values(day)) {
        const slotsArr = Array.isArray(slots) ? slots : (slots ? [slots] : []);
        for (const meal of slotsArr) {
          for (const ing of meal.ingredients || []) {
            if (ing.name) {
              const key = ing.name.toLowerCase().trim();
              needs[key] = (needs[key] || 0) + ing.quantity;
            }
          }
        }
      }
    }
    return needs;
  }, []);

  // Local-only: update pantry need values from meals without a server round-trip.
  // The server always recomputes needs from meals on GET, so this is purely
  // for immediate UI feedback after a meal save.
  const syncPantryNeeds = useCallback((mealsData, currentPantry) => {
    const needs = computeNeeds(mealsData);
    const currentItems = currentPantry?.items || [];
    const updatedItems = currentItems.map((item) => ({
      ...item,
      need: needs[item.name.toLowerCase().trim()] || 0,
    }));
    setPantry((prev) => prev ? { ...prev, items: updatedItems } : prev);
  }, [computeNeeds]);

  const mealSaveSeq = useRef({});
  const prevRankXp = useRef(null);
  const prevRankKey = useRef(null);

  const refreshRankAfterSave = useCallback(async () => {
    if (!activeKitchen) return;
    try {
      const res = await apiFetch(`/api/kitchens/${activeKitchen.id}/ranks`);
      const data = await res.json();
      const prevXp = prevRankXp.current;
      if (prevXp !== null && data.xp > prevXp) {
        const gained = data.xp - prevXp;
        setXpToast(gained);
        setTimeout(() => setXpToast(null), 1500);
        const rankedUp = prevRankKey.current && data.rank !== prevRankKey.current;
        if (navigator.vibrate) {
          navigator.vibrate(rankedUp ? [100, 50, 150] : 50);
        }
      }
      prevRankXp.current = data.xp;
      prevRankKey.current = data.rank;
      setRankData(data);
    } catch (err) {
      // silently ignore rank refresh failures
    }
  }, [apiFetch, activeKitchen]);

  // Initialize prevRankXp when rankData first loads
  useEffect(() => {
    if (rankData && prevRankXp.current === null) {
      prevRankXp.current = rankData.xp;
      prevRankKey.current = rankData.rank;
    }
  }, [rankData]);

  const handleSaveMeal = async (day, mealType, slotIndex, meal) => {
    const key = `${day}-${mealType}`;
    const seq = (mealSaveSeq.current[key] || 0) + 1;
    mealSaveSeq.current[key] = seq;

    try {
      const currentSlots = meals[day]?.[mealType];
      const slotsArr = Array.isArray(currentSlots) ? currentSlots : (currentSlots ? [currentSlots] : [{ name: '', ingredients: [] }]);
      const updatedSlots = slotsArr.map((s, i) => i === slotIndex ? meal : s);

      const res = await apiFetch(`/api/meals/${day}/${mealType}?week=${week}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSlots),
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      if (mealSaveSeq.current[key] !== seq) return;
      const updatedMeals = {
        ...meals,
        [day]: { ...meals[day], [mealType]: saved },
      };
      setMeals(updatedMeals);
      syncPantryNeeds(updatedMeals, pantry);
      refreshRankAfterSave();
    } catch (err) {
      console.error('Failed to save meal:', err);
    }
  };

  const handleAddMealSlot = async (day, mealType) => {
    const currentSlots = meals[day]?.[mealType];
    const slotsArr = Array.isArray(currentSlots) ? currentSlots : (currentSlots ? [currentSlots] : []);
    const updatedSlots = [...slotsArr, { name: '', ingredients: [] }];
    try {
      const res = await apiFetch(`/api/meals/${day}/${mealType}?week=${week}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSlots),
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      setMeals((prev) => ({
        ...prev,
        [day]: { ...prev[day], [mealType]: saved },
      }));
    } catch (err) {
      console.error('Failed to add meal slot:', err);
    }
  };

  const handleRemoveMealSlot = async (day, mealType, slotIndex) => {
    const currentSlots = meals[day]?.[mealType];
    const slotsArr = Array.isArray(currentSlots) ? currentSlots : (currentSlots ? [currentSlots] : []);
    if (slotsArr.length <= 1) return;
    const updatedSlots = slotsArr.filter((_, i) => i !== slotIndex);
    try {
      const res = await apiFetch(`/api/meals/${day}/${mealType}?week=${week}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSlots),
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      setMeals((prev) => ({
        ...prev,
        [day]: { ...prev[day], [mealType]: saved },
      }));
    } catch (err) {
      console.error('Failed to remove meal slot:', err);
    }
  };

  const handleMoveMeal = async (source, target) => {
    if (source.day === target.day && source.mealType === target.mealType && source.slotIndex === target.slotIndex) return;

    const sourceSlots = [...(meals[source.day]?.[source.mealType] || [{ name: '', ingredients: [] }])];
    const sourceMealData = { ...(sourceSlots[source.slotIndex] || { name: '', ingredients: [] }) };
    if (!sourceMealData.name) return;

    const sameSlotGroup = source.day === target.day && source.mealType === target.mealType;
    const targetSlots = sameSlotGroup
      ? sourceSlots
      : [...(meals[target.day]?.[target.mealType] || [{ name: '', ingredients: [] }])];
    const targetMealData = { ...(targetSlots[target.slotIndex] || { name: '', ingredients: [] }) };

    sourceSlots[source.slotIndex] = targetMealData;
    targetSlots[target.slotIndex] = sourceMealData;

    const prevMeals = meals;
    const updatedMeals = { ...meals };
    updatedMeals[source.day] = { ...updatedMeals[source.day], [source.mealType]: sourceSlots };
    if (!sameSlotGroup) {
      updatedMeals[target.day] = { ...updatedMeals[target.day], [target.mealType]: targetSlots };
    }
    setMeals(updatedMeals);

    try {
      if (sameSlotGroup) {
        const res = await apiFetch(`/api/meals/${source.day}/${source.mealType}?week=${week}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(sourceSlots),
        });
        if (!res.ok) throw new Error('Save failed');
        const saved = await res.json();
        setMeals((prev) => ({ ...prev, [source.day]: { ...prev[source.day], [source.mealType]: saved } }));
        syncPantryNeeds({ ...meals, [source.day]: { ...meals[source.day], [source.mealType]: saved } }, pantry);
      } else {
        const [res1, res2] = await Promise.all([
          apiFetch(`/api/meals/${source.day}/${source.mealType}?week=${week}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(sourceSlots),
          }),
          apiFetch(`/api/meals/${target.day}/${target.mealType}?week=${week}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(targetSlots),
          }),
        ]);
        if (!res1.ok || !res2.ok) throw new Error('Save failed');
        const [saved1, saved2] = await Promise.all([res1.json(), res2.json()]);
        const finalMeals = { ...meals };
        finalMeals[source.day] = { ...finalMeals[source.day], [source.mealType]: saved1 };
        finalMeals[target.day] = { ...finalMeals[target.day], [target.mealType]: saved2 };
        setMeals(finalMeals);
        syncPantryNeeds(finalMeals, pantry);
      }
    } catch (err) {
      console.error('Failed to move meal:', err);
      setMeals(prevMeals);
    }
  };

  const pantrySaveSeq = useRef(0);

  const handleSavePantry = async (items) => {
    const seq = ++pantrySaveSeq.current;
    try {
      const stock = items
        .filter((i) => i && (i.is_from_stock !== false || i.is_dismissed_missing === true))
        .map((i) => ({
          name: i.name,
          qty: i.qty || 0,
          section: i.section || 'cupboard',
          is_staple: !!i.is_staple,
          is_dismissed_missing: i.is_dismissed_missing === true,
        }));
      const res = await apiFetch(`/api/pantry?week=${week}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock }),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Failed to save pantry');
      }
      const saved = await res.json();
      if (pantrySaveSeq.current !== seq) return;
      setPantry((prev) => ({ ...prev, items: mergeStockAndNeeds(saved.stock, saved.needs) }));
      setPantrySaveError('');
    } catch (err) {
      setPantrySaveError(err.message || 'Failed to save pantry');
      console.error('Failed to save pantry:', err);
    }
  };

  const handleAddToPantry = async (ingredientName) => {
    const newItem = { name: ingredientName, qty: 0, need: 0, section: 'cupboard', is_staple: false, is_from_stock: true };
    const updated = [...(pantry.items || []), newItem];
    await handleSavePantry(updated);
    refreshRankAfterSave();
  };

  const handleConfirmWeek = async () => {
    try {
      const res = await apiFetch(`/api/pantry/confirm?week=${week}`, { method: 'POST' });
      if (res.ok) {
        setPantry((prev) => ({ ...prev, confirmed: true }));
      }
    } catch (err) {
      console.error('Failed to confirm week:', err);
    }
  };

  const handleBuyItems = async (purchases) => {
    const currentItems = pantry?.items || [];
    const updated = currentItems.map((item) => {
      const purchase = purchases.find((p) => p.name === item.name);
      if (purchase) {
        return { ...item, qty: (parseFloat(item.qty) || 0) + purchase.qty };
      }
      return item;
    });
    await handleSavePantry(updated);
  };

  const handleReceiptImport = async (imports) => {
    const currentItems = [...(pantry?.items || [])];
    for (const imp of imports) {
      if (imp.type === 'update') {
        const idx = currentItems.findIndex(it => it.name === imp.pantryName);
        if (idx >= 0) {
          currentItems[idx] = { ...currentItems[idx], qty: (parseFloat(currentItems[idx].qty) || 0) + imp.addQty };
        }
      } else if (imp.type === 'create') {
        const exists = currentItems.some(it => it.name.toLowerCase().trim() === imp.name.toLowerCase().trim());
        if (!exists) {
          currentItems.push({ name: imp.name, qty: imp.qty, need: 0, section: imp.section || 'cupboard', is_staple: false, is_from_stock: true });
        } else {
          const idx = currentItems.findIndex(it => it.name.toLowerCase().trim() === imp.name.toLowerCase().trim());
          currentItems[idx] = { ...currentItems[idx], qty: (parseFloat(currentItems[idx].qty) || 0) + imp.qty };
        }
      }
    }
    await handleSavePantry(currentItems);
  };

  const handleShareList = async () => {
    setShareLoading(true);
    try {
      const res = await apiFetch(`/api/shopping-list/share-link?week=${week}`, { method: 'POST' });
      if (!res.ok) throw new Error('Failed to generate link');
      const { url } = await res.json();
      await navigator.clipboard.writeText(url);
      setShareToast(t('shopping.linkCopied'));
      setTimeout(() => setShareToast(null), 3000);
    } catch (err) {
      console.error('Failed to share list:', err);
      setShareToast(t('shopping.shareFailed'));
      setTimeout(() => setShareToast(null), 3000);
    } finally {
      setShareLoading(false);
    }
  };

  const shortageCount = useMemo(() => {
    if (!Array.isArray(pantry?.items)) return 0;
    return pantry.items.filter((item) => {
      const effectiveNeed = item.need || 0;
      return effectiveNeed - (item.qty || 0) > 0;
    }).length;
  }, [pantry]);

  const handleAddRecipe = async (recipe) => {
    try {
      const res = await apiFetch(`/api/recipes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recipe),
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        console.error('Failed to add recipe:', res.status, errData);
        return false;
      }
      await fetchRecipes();
      refreshRankAfterSave();
      return true;
    } catch (err) {
      console.error('Failed to add recipe:', err);
      return false;
    }
  };

  const handleUpdateRecipe = async (recipe) => {
    try {
      const res = await apiFetch(`/api/recipes/${recipe.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(recipe),
      });
      if (!res.ok) throw new Error('Update failed');
      await fetchRecipes();
    } catch (err) {
      console.error('Failed to update recipe:', err);
    }
  };

  const handleDeleteRecipe = async (id) => {
    try {
      const res = await apiFetch(`/api/recipes/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      await fetchRecipes();
    } catch (err) {
      console.error('Failed to delete recipe:', err);
    }
  };

  const handleQuickSlot = useCallback(async (day, mealType, recipe) => {
    if (!meals) return;
    const meal = { name: recipe.name, ingredients: recipe.ingredients.map((ing) => ({ ...ing })) };
    const currentSlots = meals[day]?.[mealType];
    const slotsArr = Array.isArray(currentSlots) ? currentSlots : (currentSlots ? [currentSlots] : [{ name: '', ingredients: [] }]);
    // Find first empty slot, or append
    let slotIndex = slotsArr.findIndex((s) => !s.name?.trim());
    if (slotIndex === -1) slotIndex = 0;
    const updatedSlots = slotsArr.map((s, i) => i === slotIndex ? meal : s);
    try {
      const res = await apiFetch(`/api/meals/${day}/${mealType}?week=${week}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedSlots),
      });
      if (!res.ok) throw new Error('Save failed');
      const saved = await res.json();
      const updatedMeals = { ...meals, [day]: { ...meals[day], [mealType]: saved } };
      setMeals(updatedMeals);
      syncPantryNeeds(updatedMeals, pantry);
      setActiveTab('planner');
      setPlannerView('week');
    } catch (err) {
      console.error('Failed to quick-slot recipe:', err);
    }
  }, [meals, week, apiFetch, syncPantryNeeds, pantry]);

  const dates = getDatesForWeek(week);

  const showWelcome = !welcomeDismissed && meals && pantry
    && DAYS.every(day => MEAL_TYPES.every(mt => {
      const slots = meals[day]?.[mt];
      const arr = Array.isArray(slots) ? slots : (slots ? [slots] : []);
      return !arr.some(s => s.name?.trim());
    }))
    && (!pantry.items || pantry.items.length === 0);

  const dismissWelcome = () => {
    localStorage.setItem('ratatuil_onboarding_done', 'true');
    setWelcomeDismissed(true);
  };

  const handleWelcomePlanMeals = () => {
    dismissWelcome();
    setActiveTab('planner');
    setPlannerView('week');
    // Focus today's lunch input after render
    setTimeout(() => {
      const dayIndex = (new Date().getDay() + 6) % 7; // 0=Mon
      const todayKey = DAYS[dayIndex];
      const el = document.querySelector(`[data-meal-loc="${todayKey}-lunch-0"] input`);
      if (el) { el.scrollIntoView({ behavior: 'smooth', block: 'center' }); el.focus(); }
    }, 100);
  };

  const handleWelcomeStockPantry = () => {
    dismissWelcome();
    setActiveTab('planner');
    setPlannerView('week');
    setPantryView('pantry');
    // On mobile the pantry is below; scroll to it
    setTimeout(() => {
      const el = document.querySelector('.pantry-column');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const totalSlots = DAYS.length * MEAL_TYPES.length;
  const filledSlots = meals
    ? DAYS.reduce((count, day) =>
        count + MEAL_TYPES.reduce((c, mt) => {
          const slots = meals[day]?.[mt];
          const slotsArr = Array.isArray(slots) ? slots : (slots ? [slots] : []);
          return c + (slotsArr.some((s) => s.name?.trim()) ? 1 : 0);
        }, 0)
      , 0)
    : 0;
  const progressPct = totalSlots > 0 ? Math.round((filledSlots / totalSlots) * 100) : 0;

  const LANGUAGES = [
    { code: 'en', flag: '🇬🇧', label: 'English' },
    { code: 'fr', flag: '🇫🇷', label: 'Français' },
    { code: 'it', flag: '🇮🇹', label: 'Italiano' },
  ];

  const languageSelector = (
    <select
      className="language-dropdown"
      value={i18n.resolvedLanguage}
      onChange={(e) => i18n.changeLanguage(e.target.value)}
      aria-label="Language"
    >
      {LANGUAGES.map(({ code, flag, label }) => (
        <option key={code} value={code}>{flag} {label}</option>
      ))}
    </select>
  );

  if (loading) {
    return (
      <div className="app">
        <div className="loading">
          <img src="/icons/icon-animated.gif" alt="" className="loading-icon" />
          <span>{t('app.loading')}</span>
        </div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="app landing-page">
        <div className="login-screen">
          <h1><img src="/apple-touch-icon.png" alt="" className="brand-icon" />{t('app.brand')}</h1>
          <p className="login-tagline">{tagline}</p>
          <p className="login-subtitle">{t('landing.heroHeadline')}</p>
          <p className="login-hero-subtext">{t('landing.heroSubtext')}</p>

          {authSuccess ? (
            <div className="login-auth-section">
              <p className="login-request-success">{authSuccess}</p>
              <button className="login-link-btn" onClick={() => switchAuthMode('login')}>
                {t('auth.backToSignIn')}
              </button>
            </div>
          ) : (
            <div className="login-auth-section">
              <button className="google-login-btn google-login-btn--primary" onClick={signInWithGoogle}>
                {t('auth.signInWithGoogle')}
              </button>

              {!showEmailForm ? (
                <button className="login-email-toggle" onClick={() => setShowEmailForm(true)}>
                  {t('auth.continueWithEmail')}
                </button>
              ) : (
                <>
                  <div className="login-divider"><span>{t('auth.or')}</span></div>
                  {authMode === 'login' ? (
                    <>
                      <form className="login-auth-form" onSubmit={handleEmailSignIn}>
                        <input type="email" required placeholder={t('auth.emailPlaceholder')} value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} className="login-request-input" autoComplete="email" />
                        <input type="password" required placeholder={t('auth.passwordPlaceholder')} value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} className="login-request-input" autoComplete="current-password" />
                        {!isLocalhost && <div ref={turnstileRef} className="turnstile-container"></div>}
                        {authError && <p className="login-request-error">{authError}</p>}
                        <button type="submit" className="login-request-btn login-request-btn--secondary" disabled={authLoading}>
                          {authLoading ? t('auth.signingIn') : t('auth.signIn')}
                        </button>
                      </form>
                      <p className="login-switch-text">
                        {t('auth.noAccount')}{' '}
                        <button className="login-link-btn" onClick={() => switchAuthMode('register')}>
                          {t('auth.createAccount')}
                        </button>
                      </p>
                    </>
                  ) : (
                    <>
                      <form className="login-auth-form" onSubmit={handleEmailSignUp}>
                        <input type="email" required placeholder={t('auth.emailPlaceholder')} value={authEmail} onChange={(e) => setAuthEmail(e.target.value)} className="login-request-input" autoComplete="email" />
                        <input type="password" required placeholder={t('auth.passwordPlaceholder')} value={authPassword} onChange={(e) => setAuthPassword(e.target.value)} className="login-request-input" autoComplete="new-password" />
                        <input type="password" required placeholder={t('auth.confirmPasswordPlaceholder')} value={authConfirm} onChange={(e) => setAuthConfirm(e.target.value)} className="login-request-input" autoComplete="new-password" />
                        {!isLocalhost && authMode === 'register' && <div ref={turnstileRef} className="turnstile-container"></div>}
                        {authError && <p className="login-request-error">{authError}</p>}
                        <button type="submit" className="login-request-btn login-request-btn--secondary" disabled={authLoading}>
                          {authLoading ? t('auth.signingUp') : t('auth.createAccount')}
                        </button>
                      </form>
                      <p className="login-switch-text">
                        {t('auth.alreadyHaveAccount')}{' '}
                        <button className="login-link-btn" onClick={() => switchAuthMode('login')}>
                          {t('auth.signIn')}
                        </button>
                      </p>
                    </>
                  )}
                </>
              )}
            </div>
          )}

        </div>

        <div className="landing-features">
          <div className="landing-feature">
            <div className="landing-feature-img">
              <img src="/screenshots/feature-planner.png" alt={t('landing.plannerTitle')} loading="lazy" />
            </div>
            <div className="landing-feature-text">
              <h2>{t('landing.plannerTitle')}</h2>
              <p>{t('landing.plannerDesc')}</p>
            </div>
          </div>

          <div className="landing-feature landing-feature--reverse">
            <div className="landing-feature-img">
              <img src="/screenshots/feature-shopping.png" alt={t('landing.shoppingTitle')} loading="lazy" />
            </div>
            <div className="landing-feature-text">
              <h2>{t('landing.shoppingTitle')}</h2>
              <p>{t('landing.shoppingDesc')}</p>
            </div>
          </div>

          <div className="landing-feature">
            <div className="landing-feature-img">
              <img src="/screenshots/feature-recipes.png" alt={t('landing.recipesTitle')} loading="lazy" />
            </div>
            <div className="landing-feature-text">
              <h2>{t('landing.recipesTitle')}</h2>
              <p>{t('landing.recipesDesc')}</p>
            </div>
          </div>

          <div className="landing-feature landing-feature--reverse">
            <div className="landing-feature-img">
              <img src="/screenshots/feature-pantry.png" alt={t('landing.pantryTitle')} loading="lazy" />
            </div>
            <div className="landing-feature-text">
              <h2>{t('landing.pantryTitle')}</h2>
              <p>{t('landing.pantryDesc')}</p>
            </div>
          </div>

          <div className="landing-cta">
            <button className="google-login-btn google-login-btn--primary" onClick={signInWithGoogle}>
              {t('landing.cta')}
            </button>
          </div>
        </div>

        <footer className="app-footer">
          <div className="footer-controls">
            <div className="footer-left">
              <SchemeToggle />
            </div>
            {languageSelector}
          </div>
          <span>{t('app.footer')}</span>
        </footer>
      </div>
    );
  }

  if (notAllowed) {
    return (
      <div className="app">
        <div className="login-screen">
          <h1><img src="/apple-touch-icon.png" alt="" className="brand-icon" />{t('app.brand')}</h1>
          <p className="login-tagline">{tagline}</p>
          <p className="login-not-allowed">{t('auth.notAllowed')}</p>
          {requestStatus === 'sent' ? (
            <p className="login-request-success">{t('auth.accessRequested')}</p>
          ) : (
            <button className="login-request-btn" onClick={() => handleRequestAccess(session.user?.email)} disabled={requestStatus === 'sending'}>
              {requestStatus === 'sending' ? t('auth.sending') : t('auth.requestAccessFor', { email: session.user?.email })}
            </button>
          )}
          {requestStatus === 'error' && <p className="login-request-error">{requestError || t('auth.somethingWrong')}</p>}
          <button className="google-login-btn" onClick={signOut} style={{ marginTop: '1rem' }}>
            {t('auth.signOut')}
          </button>
        </div>
        <footer className="app-footer">
          <div className="footer-controls">
            <div className="footer-left">
              <SchemeToggle />
            </div>
            {languageSelector}
          </div>
          <span>{t('app.footer')}</span>
        </footer>
      </div>
    );
  }

  if (!activeKitchen) {
    return (
      <div className="app">
        <div className="loading">
          <img src="/icons/icon-animated.gif" alt="" className="loading-icon" />
          <span>{t('app.settingUpKitchen')}</span>
        </div>
      </div>
    );
  }

  if (!meals || !pantry) {
    return (
      <div className="app">
        <div className="loading">
          <img src="/icons/icon-animated.gif" alt="" className="loading-icon" />
          <span>{t('app.loadingMealPlan')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-topbar">
        <div className="topbar-left">
          <span className="app-logo"><img src="/apple-touch-icon.png" alt="" className="brand-icon" />{t('app.brand')}</span>
          <span className="app-tagline">{tagline}</span>
        </div>
        <div className="topbar-right">
          <KitchenBar rankData={rankData} onRankClick={() => setShowRankDetail(true)} />
        </div>
      </header>
      <nav className="app-nav">
        {!showWelcome && <div className="nav-tabs">
          <button
            className={`nav-tab${activeTab === 'planner' && (plannerView === 'week' || plannerView === 'month') ? ' active' : ''}`}
            onClick={() => { setActiveTab('planner'); setPlannerView('week'); }}
          >
            {t('nav.week')}
          </button>
          <button
            className="nav-tab nav-tab-pantry"
            onClick={() => {
              setActiveTab('planner');
              setPlannerView('week');
              setPantryView('pantry');
              setTimeout(() => {
                const el = document.querySelector('.pantry-column');
                if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 100);
            }}
          >
            {t('nav.pantry')}
          </button>
          <button
            className={`nav-tab${activeTab === 'recipes' ? ' active' : ''}`}
            onClick={() => setActiveTab('recipes')}
          >
            {t('nav.recipes')}
          </button>
        </div>}
        {!showWelcome && activeTab === 'planner' && plannerView === 'week' && (
          <div className="week-nav-wrapper">
            <div className="week-nav">
              <button className="week-nav-btn" onClick={() => setWeek(navigateWeek(week, -1))}>
                ‹
              </button>
              <button className="week-label week-label-btn" onClick={() => {
                const monday = dates[0];
                setMonthDate({ year: monday.getUTCFullYear(), month: monday.getUTCMonth() });
                setPlannerView('month');
              }}>
                {dates[0].toLocaleDateString(i18n.language, { weekday: 'short', timeZone: 'UTC' })}{' '}{String(dates[0].getUTCDate()).padStart(2, '0')}/{String(dates[0].getUTCMonth() + 1).padStart(2, '0')}
                {' – '}
                {dates[6].toLocaleDateString(i18n.language, { weekday: 'short', timeZone: 'UTC' })}{' '}{String(dates[6].getUTCDate()).padStart(2, '0')}/{String(dates[6].getUTCMonth() + 1).padStart(2, '0')}
              </button>
              <button className="week-nav-btn" onClick={() => setWeek(navigateWeek(week, 1))}>
                ›
              </button>
            </div>
            {meals && (
              <div className="week-progress-inline">
                <div className={`week-progress-inline-fill${progressPct === 100 ? ' complete' : ''}`} style={{ width: `${progressPct}%` }} />
              </div>
            )}
          </div>
        )}
        {!showWelcome && activeTab === 'planner' && plannerView === 'month' && (
          <div className="week-nav">
            <button className="week-nav-btn" onClick={() => setMonthDate((prev) => {
              const d = new Date(Date.UTC(prev.year, prev.month - 1, 1));
              return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
            })}>
              ‹
            </button>
            <span className="week-label">
              {new Date(Date.UTC(monthDate.year, monthDate.month, 1)).toLocaleDateString(i18n.language, { month: 'long', year: 'numeric', timeZone: 'UTC' })}
            </span>
            <button className="week-nav-btn" onClick={() => setMonthDate((prev) => {
              const d = new Date(Date.UTC(prev.year, prev.month + 1, 1));
              return { year: d.getUTCFullYear(), month: d.getUTCMonth() };
            })}>
              ›
            </button>
          </div>
        )}

      </nav>
      <main className="app-main">
        <InstallPrompt />
        {showRankDetail && (
          <RankDetail rankData={rankData} onClose={() => setShowRankDetail(false)} />
        )}
        {xpToast !== null && (
          <div className="xp-toast">+{xpToast} XP</div>
        )}
        {pushPromptVisible && (
          <div className="push-prompt">
            <span>{t('push.prompt')}</span>
            <button className="push-prompt-btn accept" onClick={handleEnablePush}>{t('push.accept')}</button>
            <button className="push-prompt-btn dismiss" onClick={handleDismissPush}>{t('push.dismiss')}</button>
          </div>
        )}
        {activeTab === 'planner' ? (
          plannerView === 'month' ? (
            <div className="month-layout">
              <MonthView
                year={monthDate.year}
                month={monthDate.month}
                apiFetch={apiFetch}
                onDayClick={(date) => {
                  setWeek(getISOWeekString(date));
                  setPlannerView('week');
                }}
              />
            </div>
          ) : showWelcome ? (
            <WelcomeGuide onPlanMeals={handleWelcomePlanMeals} onStockPantry={handleWelcomeStockPantry} />
          ) : (
            <div className="main-layout">
              <section className="days-column">
                <WeekView meals={meals} pantry={pantry.items} recipes={recipes} recentWeeks={recentWeeks} dates={dates} onSave={handleSaveMeal} onAddSlot={handleAddMealSlot} onRemoveSlot={handleRemoveMealSlot} onAddRecipe={handleAddRecipe} onAddToPantry={handleAddToPantry} onMoveMeal={handleMoveMeal} />
              </section>
              <aside className="pantry-column">
                <div className="pantry-view-tabs">
                  <button
                    className={`pantry-view-tab${pantryView === 'pantry' ? ' active' : ''}`}
                    onClick={() => setPantryView('pantry')}
                  >
                    {t('pantryTab.pantry')}
                  </button>
                  <button
                    className={`pantry-view-tab${pantryView === 'shopping' ? ' active' : ''}`}
                    onClick={() => setPantryView('shopping')}
                  >
                    {t('pantryTab.shopping')}{shortageCount > 0 && <span className="shopping-badge">{shortageCount}</span>}
                  </button>
                </div>
                {pantryView === 'pantry' ? (
                  <Pantry items={pantry.items} confirmed={pantry.confirmed} saveError={pantrySaveError} onSave={handleSavePantry} onConfirm={handleConfirmWeek} onShowShopping={() => setPantryView('shopping')} />
                ) : (
                  <>
                    {shareToast && <div className="shopping-success-banner">{shareToast}</div>}
                    <ShoppingList items={pantry.items} onBuy={handleBuyItems} onShareList={handleShareList} shareLoading={shareLoading} onImportReceipt={() => setShowReceiptImport(true)} />
                  </>
                )}
              </aside>
            </div>
          )
        ) : (
          <Recipes
            recipes={recipes}
            pantryItems={pantry?.items || []}
            onAdd={handleAddRecipe}
            onUpdate={handleUpdateRecipe}
            onDelete={handleDeleteRecipe}
            onQuickSlot={handleQuickSlot}
          />
        )}
      </main>
      {showReceiptImport && (
        <ReceiptImport
          pantryItems={pantry?.items || []}
          onImport={handleReceiptImport}
          onClose={() => setShowReceiptImport(false)}
          apiFetch={apiFetch}
        />
      )}
      <footer className="app-footer">
        <div className="footer-controls">
          <div className="footer-left">
            <SchemeToggle />
          </div>
          {languageSelector}
        </div>
        <span>{t('app.footer')}</span>
      </footer>
    </div>
  );
}

export default App;
